import * as fs from "node:fs";
import { validateManifest, type ReleaseManifest } from "./release-manifest.js";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";

export const CHANNEL_LATEST_URL =
  "https://github.com/m8t-run/m8t/releases/latest/download/manifest.json";

export function channelUrlForVersion(tag: string): string {
  return `https://github.com/m8t-run/m8t/releases/download/${tag}/manifest.json`;
}

export type ManifestSource =
  | { file: string }
  | { url: string }
  | { channel: true; version?: string };

interface Deps {
  fetchImpl?: typeof fetch;
  ghToken?: () => Promise<string>;
  readFile?: (p: string) => string;
}

export async function fetchManifest(source: ManifestSource, deps: Deps = {}): Promise<ReleaseManifest> {
  const readFile = deps.readFile ?? ((p: string) => fs.readFileSync(p, "utf8"));
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;

  let raw: string;
  if ("file" in source) {
    try {
      raw = readFile(source.file);
    } catch (e) {
      throw new LocalCliError({ code: "PLATFORM_MANIFEST_READ_FAILED", message: `Could not read manifest file '${source.file}': ${(e as Error).message}` });
    }
  } else {
    const url = "url" in source ? source.url : source.version ? channelUrlForVersion(source.version) : CHANNEL_LATEST_URL;
    // gh token is best-effort — the day the repo goes public it becomes optional.
    let token: string | undefined;
    try { token = await ghToken(); } catch { token = undefined; }
    const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new LocalCliError({
        code: "PLATFORM_MANIFEST_FETCH_FAILED",
        message: `GET ${url} returned HTTP ${res.status.toString()}.`,
        hint: "The release channel is auth-gated until the repo is public. Run 'gh auth login', or pass --manifest-file / --manifest-url.",
      });
    }
    raw = JSON.stringify(await res.json());
  }

  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_MANIFEST_PARSE_FAILED", message: `Manifest is not valid JSON: ${(e as Error).message}` });
  }
  const errors = validateManifest(parsed);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_MANIFEST_INVALID",
      message: `Fetched manifest failed validation:\n  - ${errors.join("\n  - ")}`,
    });
  }
  return parsed as ReleaseManifest;
}
